import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Alert, Box, Button, Header, SpaceBetween } from '@amzn/awsui-components-react';
import { MDAWorksheetSelectorFormState } from './MDAWorksheetSelector';
import { MultiAllocationWorksheetData } from 'src/models/multidimensional-allocation/MultiAllocationWorksheetData';
import TPPolarisTable from 'src/components/shared/TPPolarisTable';
import { GlobalAppContext } from 'src/components/App';
import { TableProps } from '@amzn/awsui-components-react/polaris/table';
import { WizardStepComponent } from './MDAWorksheetSteps';
import { PropertyFilterProperty } from '@amzn/awsui-collection-hooks';
import { useEarlierGroupMdaEnabled } from 'src/hooks/useEarlierGroupMdaEnabled';
import 'src/assets/styles/react-table.scss';

const TABLE_COLUMNS = {
    WORKSHEET_NAME: {
        id: 'worksheetName',
        label: 'Names'
    },
    LAST_EXECUTION_DATE: {
        id: 'lastUpdatedOn', // Todo! update once backend starts sending this info.
        label: 'Last Execution Time'
    }
}

// Step 1: MDA Worksheet Selection Table
export const WorksheetSelectionTable: WizardStepComponent<MDAWorksheetSelectorFormState> = (props) => {
    const { services } = useContext(GlobalAppContext);
    const [selectedWorksheets, setSelectedWorksheets] = useState<MultiAllocationWorksheetData[]>([]);
    const executionGroupNumber = props.executionGroupNumber;
    const fetchWorksheetsRequest = useMemo(() => ({
        groupNumber: executionGroupNumber
    }), [executionGroupNumber])

    const [worksheetResponse, worksheetLoading, worksheetError] =
        services.multidimensionalAllocationService.getMultiAllocationWorksheets('fetch', fetchWorksheetsRequest);

    // Flag on and the CLI has a group: a second list call adds the worksheets of groups that MEC lists before it.
    const earlierGroupMdaEnabled = useEarlierGroupMdaEnabled();
    const listsOtherGroups = earlierGroupMdaEnabled === true && !!executionGroupNumber;
    const availableWorksheetsRequest = useMemo(() => ({
        availableToGroupNumber: executionGroupNumber
    }), [executionGroupNumber]);
    const [allGroupsResponse, allGroupsLoading, allGroupsError] =
        services.multidimensionalAllocationService.getMultiAllocationWorksheets(
            listsOtherGroups ? 'fetch' : '', availableWorksheetsRequest);
    const otherGroupWorksheets = useMemo(() => {
        const ownIds = new Set(worksheetResponse?.worksheets?.map((worksheet) => worksheet.worksheetId));
        return allGroupsResponse?.worksheets?.filter((worksheet) => !ownIds.has(worksheet.worksheetId)) ?? [];
    }, [worksheetResponse, allGroupsResponse]);

    // Restore previously selected worksheet from initialData
    useEffect(() => {
        if (props.initialData?.worksheetId && worksheetResponse?.worksheets && earlierGroupMdaEnabled !== undefined) {
            const previouslySelectedWorksheet = [...worksheetResponse.worksheets, ...otherGroupWorksheets].find(
                worksheet => worksheet.worksheetId === props.initialData?.worksheetId
            );
            
            if (previouslySelectedWorksheet) {
                setSelectedWorksheets([previouslySelectedWorksheet]);
            } else if (!listsOtherGroups || allGroupsResponse) {
                // Throw error if initialData has worksheetId but worksheet is not found
                throw new Error(`Previously selected worksheet with ID "${props.initialData.worksheetId}" was not found in the current worksheet list. The worksheet may have been deleted or is no longer accessible.`);
            }
        }
    }, [props.initialData?.worksheetId, props.initialData?.worksheetVersion, worksheetResponse?.worksheets,
        earlierGroupMdaEnabled, listsOtherGroups, otherGroupWorksheets, allGroupsResponse]);

    // Column definitions for the table - only worksheetName and last execution time
    const columnDefinitions: TableProps.ColumnDefinition<MultiAllocationWorksheetData>[] = [
        {
            id: TABLE_COLUMNS.WORKSHEET_NAME.id,
            header: TABLE_COLUMNS.WORKSHEET_NAME.label,
            cell: (item) => item.worksheetName,
            sortingField: TABLE_COLUMNS.WORKSHEET_NAME.id,
        },
        {
            id: TABLE_COLUMNS.LAST_EXECUTION_DATE.id,
            header: TABLE_COLUMNS.LAST_EXECUTION_DATE.label,
            cell: (item) => item.lastUpdatedOn,
            sortingField: TABLE_COLUMNS.LAST_EXECUTION_DATE.id,
        },
    ];

    const handleNext = () => {
        if (selectedWorksheets.length > 0) {
            const stepData = {
                worksheetId: selectedWorksheets[0].worksheetId,
                worksheetVersion: selectedWorksheets[0].worksheetVersion,
                worksheetName: selectedWorksheets[0].worksheetName
            };
            props.onSave(stepData);
        }
    };

    const worksheets = [...(worksheetResponse?.worksheets ?? []), ...otherGroupWorksheets].filter(worksheet => worksheet.status === 'ACTIVE');
    const hasSelectedWorksheet = selectedWorksheets.length > 0;

    // Define filtering properties for search functionality
    const filteringProperties: PropertyFilterProperty[] = [
        {
            key: TABLE_COLUMNS.WORKSHEET_NAME.id,
            operators: ['=', '!=', ':', '!:'],
            propertyLabel: TABLE_COLUMNS.WORKSHEET_NAME.label,
            groupValuesLabel: 'Worksheet names',
        },
    ];

    // Define visible content options for preferences
    const visibleContentOptions = Object.values(TABLE_COLUMNS).map((column) => ({
        id: column.id,
        label: column.label,
        editable: false,
        
    }))

    const defaultPreferences = {
        pageSize: 10,
        visibleColumns: ['worksheetName', 'lastUpdatedOn'],
        wrapLines: false,
        stripedRows: false,
        stickyColumns: { first: 0, last: 0 },
    };

    // Empty state when no worksheets
    const tableEmptyState = worksheetError ? (
        <Box textAlign="center">
            <Alert type="error" header="Unable to load worksheets">
                {worksheetError}
            </Alert>
        </Box>
    ) : !executionGroupNumber ?
        <Box textAlign='center'>
            <Alert type='warning' header="CLI is not assigned to a execution group">
                Assign a group before proceeding. <i>You may make the selection in the previous step</i>
            </Alert>
        </Box>
    
      : (
        <Box textAlign="center">
            <SpaceBetween direction="vertical" size="s">
                <b>No worksheets found</b>
                <span>No worksheets are available for selection.</span>
            </SpaceBetween>
        </Box>
    );

    const footer = (
            <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={props.onCancel}>
                    Cancel
                </Button>

                <Button onClick={handleNext} disabled={!hasSelectedWorksheet}>
                    Next
                </Button>
            </SpaceBetween>
    );

    return (
        <Box padding={{top: 'l', bottom: 'l', left: 'xxl', right: 'xxl'}}> 
        {allGroupsError && (
            <Alert type="warning" header="Unable to load worksheets of other groups">
                {allGroupsError}
            </Alert>
        )}
        <TPPolarisTable
            contentDensity='compact'
            variant='borderless'
            header={
                <Header
                    variant='h3'
                    description={listsOtherGroups
                        ? 'Shows the MDA worksheets of this group and of groups that MEC lists before it.'
                        : undefined}
                > Multidimensional Allocation Worksheets </Header>
            }
            columnDefinitions={columnDefinitions}
            tableItems={worksheets}
            isLoading={worksheetLoading || allGroupsLoading}
            loadingText="Loading worksheets..."
            tableEmptyState={tableEmptyState}
            resizableColumns={true}
            sortingColumn="worksheetName"
            filteringProperties={filteringProperties}
            filteringEmptyText="No worksheets found"
            filteringLoadingText="Loading worksheets..."
            preferencesStorageKey="mda-worksheet-selection-table"
            selectionType="single"
            selectedItems={selectedWorksheets}
            setSelectedItems={({ detail }) => {
                setSelectedWorksheets(detail.selectedItems);
            }}
            visibleContentOptions={visibleContentOptions}
            defaultPreferences={defaultPreferences}
            pageSizeOptions={[
                { value: 10, label: '10 worksheets' },
                { value: 25, label: '25 worksheets' },
                { value: 50, label: '50 worksheets' },
            ]}
            footer={footer}
        />
        </Box>
    );
};
